import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, CreditCard, TrendingUp, Menu, UserCircle, Wallet, Home } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { ROUTE_PATHS } from '../../_pages/_utils/router.utils';
import { useUserContext } from '../../../providers/user/user.provider';

const DashboardPageNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserContext();
  const { t } = useLingui();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const hasLoans = !!user?.hasActiveFranklinLoan;

  const navItems = [
    { label: t`Dashboard`, icon: LayoutDashboard, path: ROUTE_PATHS.DASHBOARD },
    ...(hasLoans
      ? [{ label: t`Loans`, icon: CreditCard, path: ROUTE_PATHS.LOANS_MANAGEMENT }]
      : [{ label: t`Debts`, icon: Wallet, path: ROUTE_PATHS.MY_DEBTS }]),
    { label: t`Score`, icon: TrendingUp, path: ROUTE_PATHS.CREDIT_SCORE },
  ];

  const menuItems = [
    { label: t`Account`, icon: UserCircle, path: ROUTE_PATHS.ACCOUNT },
    ...(hasLoans ? [{ label: t`Debts`, icon: Wallet, path: ROUTE_PATHS.MY_DEBTS }] : []),
    { label: t`Rent Reporting`, icon: Home, path: ROUTE_PATHS.RENT_REPORTING },
  ];

  const isActive = (path: string) => {
    if (path === ROUTE_PATHS.DASHBOARD) {
      return location.pathname === ROUTE_PATHS.DASHBOARD;
    }
    return location.pathname.startsWith(path);
  };

  const isMenuItemActive = menuItems.some(({ path }) => isActive(path));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div
      ref={menuRef}
      className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 md:hidden z-[200]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {menuOpen && (
        <div className="absolute bottom-full right-0 w-50 bg-white border rounded-tl-xl overflow-hidden">
          {menuItems.map(({ label, icon: Icon, path }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors hover:bg-gray-50 ${
                isActive(path) ? 'text-blue-500' : 'text-gray-700'
              }`}
            >
              <Icon
                className={`w-5 h-5 ${isActive(path) ? 'text-blue-500' : 'text-gray-400'}`}
                strokeWidth={isActive(path) ? 2.5 : 1.75}
              />
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="flex">
        {navItems.map(({ label, icon: Icon, path }) => {
          const active = isActive(path);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`flex-1 flex flex-col items-center py-3 text-xs font-medium transition-colors ${
                active ? 'text-blue-500' : 'text-gray-500'
              }`}
            >
              <Icon
                className={`w-6 h-6 mb-1 ${active ? 'text-blue-500' : 'text-gray-400'}`}
                strokeWidth={active ? 2.5 : 1.75}
              />
              {label}
            </button>
          );
        })}

        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`flex-1 flex flex-col items-center py-3 text-xs font-medium transition-colors ${
            menuOpen || isMenuItemActive ? 'text-blue-500' : 'text-gray-500'
          }`}
        >
          <Menu
            className={`w-6 h-6 mb-1 ${menuOpen || isMenuItemActive ? 'text-blue-500' : 'text-gray-400'}`}
            strokeWidth={menuOpen || isMenuItemActive ? 2.5 : 1.75}
          />
          {t`More`}
        </button>
      </div>
    </div>
  );
};

export default DashboardPageNav;
